/**
 * Product story elements.
 *
 * <image-compare> — before/after image slider. A full-surface transparent
 * range input drives the split position, so keyboard and pointer both work
 * with no extra event plumbing.
 *
 * <product-story> — pauses autoplaying story videos while they are
 * off-screen and disables autoplay entirely for reduced-motion users.
 */

class ImageCompare extends HTMLElement {
  #controller;

  connectedCallback() {
    this.#controller = new AbortController();

    const range = this.querySelector('.image-compare__range');
    if (!range) return;

    this.#setSplit(range.value);

    range.addEventListener(
      'input',
      () => this.#setSplit(range.value),
      { signal: this.#controller.signal }
    );
  }

  disconnectedCallback() {
    this.#controller?.abort();
  }

  #setSplit(value) {
    this.style.setProperty('--split', `${value}%`);
  }
}

if (!customElements.get('image-compare')) {
  customElements.define('image-compare', ImageCompare);
}

class ProductStory extends HTMLElement {
  #controller;
  #observer;

  connectedCallback() {
    this.#controller = new AbortController();

    const videos = this.querySelectorAll('video');
    if (!videos.length) return;

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    if (reducedMotion.matches) {
      for (const video of videos) {
        video.removeAttribute('autoplay');
        video.pause();
        video.controls = true;
      }
      return;
    }

    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const video = entry.target;
          if (entry.isIntersecting) {
            video.muted = true;
            video.play().catch(() => {});
          } else {
            video.pause();
          }
        }
      },
      { rootMargin: '100px 0px' }
    );

    for (const video of videos) this.#observer.observe(video);
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#observer?.disconnect();
  }
}

if (!customElements.get('product-story')) {
  customElements.define('product-story', ProductStory);
}
